import { agentsVerificationsApi } from '../../../data/agentsVerificationsApi';
import type {
  AdmitEvaCaseResponse,
  EvaCasesQueryArgs,
  EvaCasesResponse,
  EvaCohortView,
  EvaPeriodActivityQueryArgs,
  EvaPeriodActivityView,
} from './evaCaseViews';

/**
 * eva feature endpoint for the case-centric dashboard (spec 045 US1), injected into the agents
 * module's Verifications base api. Served entirely from the `verification` container (no run
 * reads); `company` is the only required filter.
 */
const evaCasesEndpoints = agentsVerificationsApi.injectEndpoints({
  endpoints: (builder) => ({
    getEvaCases: builder.query<EvaCasesResponse, EvaCasesQueryArgs>({
      query: ({ company, status, center, payer, page, pageSize }) => ({
        url: '/verification/eva/cases',
        method: 'GET',
        params: {
          company,
          ...(status ? { status } : {}),
          ...(center ? { center } : {}),
          ...(payer ? { payer } : {}),
          ...(page ? { page } : {}),
          ...(pageSize ? { pageSize } : {}),
        },
      }),
      providesTags: (_result, _error, { company }) => [{ type: 'EvaCase', id: company }, 'EvaCase'],
    }),
    // spec 113 US7b — surgery-month cohort (up to the last 3 months by default).
    getEvaCohort: builder.query<EvaCohortView, { company: string; months?: number }>({
      query: ({ company, months }) => ({
        url: '/verification/eva/cohort',
        method: 'GET',
        params: { company, ...(months ? { months } : {}) },
      }),
      // A distinct tag from `EvaCase`: `admitEvaCase` invalidates `EvaCase` on every Reprocess Now
      // click, and reusing that tag here would trigger a fresh cohort recount — a read of up to
      // 50,000 case documents — on every one of those clicks.
      providesTags: (_result, _error, { company }) => [{ type: 'EvaCohort', id: company }],
    }),
    // spec 211 US1 — grouped counts for a processing-date period (no case rows).
    getEvaPeriodActivity: builder.query<EvaPeriodActivityView, EvaPeriodActivityQueryArgs>({
      query: ({ company, from, to }) => ({
        url: '/verification/eva/activity',
        method: 'GET',
        params: { company, from, to },
      }),
      // Its own tag, for the same reason the cohort has one: `admitEvaCase` invalidates `EvaCase` on
      // every Reprocess Now click, and sharing that tag would re-read the whole period each time.
      providesTags: (_result, _error, { company }) => [{ type: 'EvaActivity', id: company }],
    }),
    // spec 045 US5 — Reprocess Now (direct-admission, bypasses both due-query arms).
    admitEvaCase: builder.mutation<AdmitEvaCaseResponse, { id: string }>({
      query: ({ id }) => ({
        url: '/verification/eva/admit',
        method: 'POST',
        body: { id, ingest: true },
      }),
      invalidatesTags: ['EvaCase'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEvaCasesQuery,
  useLazyGetEvaCasesQuery,
  useGetEvaCohortQuery,
  useGetEvaPeriodActivityQuery,
  useAdmitEvaCaseMutation,
} = evaCasesEndpoints;
